import { useState } from 'react';
import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import toast from 'react-hot-toast';
import SEO from '../components/shared/SEO';
import { submitContact } from '../api';
import { formatSubsidiaryName, getSubsidiaryUrl } from '../constants/subsidiaryLinks';
import { useSiteConfig } from '../hooks/useApi';
import '../styles/investorpage.css';

const subsidiaries = [
  { name: 'DeFi Assets LLC', type: 'Decentralized Finance Infrastructure', jurisdiction: 'United States' },
  { name: 'Vivasvat Revolutions Limited', type: 'Technology & Innovation Ventures', jurisdiction: 'International' },
  { name: 'AI Adya LLC', type: 'Artificial Intelligence & Data', jurisdiction: 'United States' },
  { name: 'Crypto Asset Rating LLC', type: 'Analytics & Risk Intelligence', jurisdiction: 'United States' },
];

const inquiryTypes = [
  'Investment Inquiry',
  'Partnership Opportunity',
  'Media / Press',
  'General Inquiry',
];

type FormState = { name: string; company: string; email: string; type: string; message: string };
type FieldErrors = Partial<Record<'name' | 'email' | 'message', string>>;

const emptyForm: FormState = { name: '', company: '', email: '', type: '', message: '' };

function validateForm(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.name.trim() || form.name.trim().length < 2) errors.name = 'Full name is required';
  if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'A valid email address is required';
  if (!form.message.trim() || form.message.trim().length < 10) errors.message = 'Message must be at least 10 characters';
  return errors;
}

const DEFAULT_STEPS = [
  { title: 'Create Investor Account', description: 'Register on the Apex investor portal. Complete your accredited investor questionnaire. Takes less than 5 minutes.' },
  { title: 'Complete KYC & Verification', description: 'Complete KYC/AML identity verification and accredited investor qualification via Acuant\'s automated compliance platform.' },
  { title: 'Sign Documents & Fund', description: 'Review PPM and Subscription Agreement. Execute via Eversign. Fund via wire, USDC, or USDT. Receive tokens upon offering close.' },
];

const DEFAULT_VAL_STATS = [
  { value: '0.6%', label: 'of Carta\'s valuation ($12B cap table platform)' },
  { value: '0.35%', label: 'of SS&C Technologies ($20B alt investment admin)' },
  { value: '0.3%', label: 'of Broadridge Financial ($25B capital markets infra)' },
  { value: '2.2%', label: 'of Securitize ($3B tokenization peer)' },
];

export default function InvestorPage() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const { data: config } = useSiteConfig();

  const ipSubtitle = config?.investmentProcess?.subtitle || 'A fully digital process. No paperwork, no in-person meetings required. Complete all steps online in one session.';
  const ipSteps = config?.investmentProcess?.steps?.length ? config.investmentProcess.steps : DEFAULT_STEPS;
  const valTitle = config?.valuationSection?.title || 'Why Our $60M–$75M Valuation Is Grounded';
  const valStats = config?.valuationSection?.stats?.length ? config.valuationSection.stats : DEFAULT_VAL_STATS;
  const valDisclaimer = config?.valuationSection?.disclaimer || '';

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (field in fieldErrors) setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setLoading(true);
    try {
      await submitContact({ name: form.name, email: form.email, company: form.company || undefined, message: form.message, inquiryType: form.type || undefined, source: 'investor' });
      toast.success('Inquiry submitted successfully. Our team will be in touch.');
      setForm(emptyForm);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="investor-page">
        <SEO
          title="Investor Relations - Apex DeFi Labs Inc."
          description="Apex DeFi Labs Inc. investor relations - corporate structure, governance, and partnership opportunities. Connect with our team for institutional inquiries."
          url="https://apexholdings.com/investor-relations"
          type="website"
          keywords="Apex DeFi Labs Inc. investors, DeFi investment, institutional blockchain, digital asset holding company"
        />

        {/* Hero */}
        <section className="investor-page-hero">
          <div className="investor-page-hero-overlay" />
          <div className="investor-page-shell">
            <SectionLabel>Investor Relations</SectionLabel>
            <h1 className="investor-page-title">
              Partner With <em>Apex</em>
            </h1>
            <p className="investor-page-subtitle">
              Explore investment opportunities, strategic partnerships, and collaboration with Apex DeFi Labs Inc.
            </p>
          </div>
        </section>

        {/* How to Invest — 3 Steps */}
        <section className="investor-process-section">
          <div className="investor-page-shell">
            <div className="investor-process-header">
              <div className="eyebrow-label">How to Invest</div>
              <h2 className="investor-process-title">
                Three Steps to Complete <em>Your Investment</em>
              </h2>
              <p className="investor-process-subtitle">{ipSubtitle}</p>
            </div>

            <div className="investor-process-steps">
              {ipSteps.map((step, i) => (
                <div key={i} className="investor-process-step">
                  <div className="ips-number">{i + 1}</div>
                  <div className="ips-connector" aria-hidden="true" />
                  <h3 className="ips-title">{step.title}</h3>
                  <p className="ips-desc">{step.description}</p>
                </div>
              ))}
            </div>

            <div className="investor-process-cta">
              <a href="mailto:info@apexdefilabs.com" className="btn-gold investor-process-btn">
                Begin Verification Now →
              </a>
              <p className="investor-process-contact">
                Questions?{' '}
                <a href="mailto:info@apexdefilabs.com">info@apexdefilabs.com</a>
                {' '}or{' '}
                <a href="tel:+18886660441">+1-888-666-0441</a>
              </p>
            </div>
          </div>
        </section>

        {/* Valuation Context */}
        <section className="investor-valuation-section">
          <div className="investor-page-shell">
            <div className="eyebrow-label" style={{ color: 'var(--gold-light)' }}>Valuation Context</div>
            <h2 className="investor-valuation-title"
              dangerouslySetInnerHTML={{ __html: valTitle.replace(/(\$[\d–$M]+\s+Valuation)/g, '<em>$1</em>') }}
            />
            <div className="investor-valuation-grid">
              {valStats.map((stat, i) => (
                <div key={i} className="investor-val-card">
                  <div className="ivc-value">{stat.value}</div>
                  <div className="ivc-label">{stat.label}</div>
                </div>
              ))}
            </div>
            {valDisclaimer && (
              <p className="investor-valuation-disclaimer">{valDisclaimer}</p>
            )}
          </div>
        </section>

        {/* Corporate Structure */}
        <section className="investor-page-content">
          <div className="investor-page-shell">
            <SectionLabel>Corporate Structure</SectionLabel>
            <h2 className="investor-page-section-title">Our Subsidiary Entities</h2>

            <div className="investor-page-subsidiary-grid">
              {subsidiaries.map((subsidiary) => {
                const subsidiaryUrl = getSubsidiaryUrl(subsidiary.name);
                const displayName = formatSubsidiaryName(subsidiary.name);
                const content = (
                  <>
                    <h3 className="investor-page-subsidiary-name">{displayName}</h3>
                    <p className="investor-page-subsidiary-type">{subsidiary.type}</p>
                    <p className="investor-page-subsidiary-jurisdiction">{subsidiary.jurisdiction}</p>
                    {subsidiaryUrl ? <div className="investor-page-subsidiary-link">Visit Site &rarr;</div> : null}
                  </>
                );
                if (!subsidiaryUrl) return <article key={subsidiary.name} className="investor-page-subsidiary-card">{content}</article>;
                return (
                  <a key={subsidiary.name} href={subsidiaryUrl} target="_blank" rel="noopener noreferrer" className="investor-page-subsidiary-card">
                    {content}
                  </a>
                );
              })}
            </div>

            {/* Investor Inquiry Form */}
            <div className="investor-page-form-block">
              <SectionLabel>Get In Touch</SectionLabel>
              <h2 className="investor-page-section-title">Investor Inquiry</h2>

              <form onSubmit={handleSubmit} className="investor-page-form">
                <div className="investor-page-form-grid">
                  <div className="investor-page-field">
                    <label className="investor-page-label">Full Name <span className="investor-page-required">*</span></label>
                    <input type="text" placeholder="Full Name" value={form.name} onChange={set('name')} className={`investor-page-input${fieldErrors.name ? ' investor-page-input--error' : ''}`} />
                    {fieldErrors.name && <span className="investor-page-field-error">{fieldErrors.name}</span>}
                  </div>
                  <div className="investor-page-field">
                    <label className="investor-page-label">Company</label>
                    <input type="text" placeholder="Company" value={form.company} onChange={set('company')} className="investor-page-input" />
                  </div>
                </div>
                <div className="investor-page-field">
                  <label className="investor-page-label">Email Address <span className="investor-page-required">*</span></label>
                  <input type="email" placeholder="Email Address" value={form.email} onChange={set('email')} className={`investor-page-input${fieldErrors.email ? ' investor-page-input--error' : ''}`} />
                  {fieldErrors.email && <span className="investor-page-field-error">{fieldErrors.email}</span>}
                </div>
                <div className="investor-page-field">
                  <label className="investor-page-label">Type of Inquiry</label>
                  <select value={form.type} onChange={set('type')} className="investor-page-input investor-page-select">
                    <option value="">Select an inquiry type</option>
                    {inquiryTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="investor-page-field">
                  <label className="investor-page-label">Message <span className="investor-page-required">*</span></label>
                  <textarea rows={5} placeholder="Message" value={form.message} onChange={set('message')} className={`investor-page-input investor-page-textarea${fieldErrors.message ? ' investor-page-input--error' : ''}`} />
                  {fieldErrors.message && <span className="investor-page-field-error">{fieldErrors.message}</span>}
                </div>
                <button type="submit" disabled={loading} className="investor-page-submit">
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
